/**
 * EntityStore: Global cache for entity METADATA only
 * 
 * 🛡️ CRITICAL DATA LOSS PREVENTION:
 * - Doc entities: Does NOT cache content (skeleton.base64/content)
 * - Always fetch fresh content when opening documents via entityService.get(id)
 * - Caching only metadata: name, id, llmId, timestamps, icon
 * 
 * WHY: EntityStore uses /api/entities (list endpoint) which strips base64 for performance.
 * If we cached this stripped content, documents would load empty when opened from cache.
 * Solution: Strip base64/content from cache, force fresh fetch when opening documents.
 */
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

// entityCRDTService removed: entity sync now flows through entity-system Y.Doc
// (gui/src/stores/entitySystemYjsStore.ts subscribeToEntitySystemChanges)
import { spaceCRDTService, SpaceUpdate } from '../services/crdt/spaceCRDTService';
import { entityService } from '../services/entityService';
import { fileService } from '../services/FileService';
import { BaseEntityType } from '../types';
import { entityCacheService } from '../services/entityCacheService';
import { getCurrentUserId } from '../utils/userIdUtils';

import { createFileLogger } from '../utils/logger';
import { createEntityActions } from './entityActions';

// ✅ PHASE 2: Memoization caches to prevent infinite re-render loops
let cachedDisplayableEntities: BaseEntityType[] = [];
let lastEntitiesRef: BaseEntityType[] | null = null;

let cachedTrashedEntities: BaseEntityType[] = [];
let lastTrashedRef: BaseEntityType[] | null = null;

// ✅ BUG FIX #2: Stable timestamps for synthetic system groups
// Using dynamic timestamps (new Date()) causes unnecessary re-renders
const STABLE_SYSTEM_TIMESTAMP = new Date('2026-01-01T00:00:00Z').toISOString();

// ⚡ PERFORMANCE FIX: Use Set for O(1) lookups instead of O(n) array.includes()
// For getDisplayableEntities and getTrashedEntities filters
// Changes 8 comparisons → 1 hash lookup (8x faster)
const HIDDEN_ENTITY_TYPES = new Set([
  'DocVersion',
  'Message',
  'CommentThread',
  'Thread',
  'Space',
  'Window',
  'SearchResults',
  'ManuscriptDiscoveryJob',
  'APIConnection',
  'Integration',      // Hide integration state entities from sidebar
  // ✅ Integration data entities: system-managed records from external services.
  // These are queryable via river.entity.query() in apps but are NOT user-organizable
  // documents — they should never appear in space sidebars.
  'BankTransaction',
  'PlaidItem',
  'PlaidTransaction',
  'PlaidAccount',
  'GoogleDriveFile',
  'Contact',         // Managed by ContactsApp — not user-organizable documents
  'Mail.Contact',    // Mail contacts — managed by ContactsApp
  'LocalFile',
  'RiverAppStorage',  // App storage buckets are internal — never shown to users
  'Task',             // Managed by TasksApp — never shown in file tree
  'Project',          // Managed by TasksApp — never shown in file tree
  'Area',             // Managed by TasksApp — never shown in file tree
  'Job',              // Automation run records — never shown in file tree
  // 'Automation' intentionally NOT hidden — shows in sidebar tree via dataspace edge
  'Schedule',         // Automation schedules — never shown in file tree
  'Mail.Message',     // Thread contents — fetched on demand by ThreadDetail via /messages endpoint, never loaded into global store (140k+ per user would destroy performance)
  'System.Memory',       // Intelligence layer — managed by consolidation pipeline, not user-organizable
  'System.UserProfile',  // Intelligence layer — derived per-user profile, managed internally
]);

interface EntityEvent {
  id: string;
  timestamp: number;
  userId: string;
  action: 'create' | 'update' | 'delete';
  entityId: string;
  data?: BaseEntityType;
  previousData?: BaseEntityType;
}

export interface EntityState {
  entities: BaseEntityType[];
  authToken: string | null;
  status: 'idle' | 'loading' | 'error';
  error?: Error;
  events: EntityEvent[];
  undoStack: EntityEvent[];
  redoStack: EntityEvent[];
  initialized: boolean;
  isObserving: boolean;  // ✅ Track if CRDT observers are active
  currentSpaceId?: string;
  initializationPromise?: Promise<void> | null;  // ✅ Track in-flight initialization
  initializationEpoch: number;  // Incremented on each new initialize() call; guards stale writes
  lifecycle: 'uninitialized' | 'loading' | 'ready' | 'error';  // ✅ NEW: Formal lifecycle state
  syncInProgress: boolean;  // ✅ NEW: Prevent concurrent staleness checks
  currentDataspaceKey?: string | null;  // ✅ ORGS V1: Track current dataspace (personal or orgId)
  dataspaceEntities: Record<string, BaseEntityType[]>;  // ✅ ORGS V1: Cache entities per dataspace
  
  // ✅ Computed state: Connected integrations (O(1) lookup for tool filtering)
  // Updated automatically in _applyLocalUpsert mutation funnel
  connectedIntegrations: Set<string>;  // e.g., Set(['Gmail', 'GoogleCalendar', 'GoogleDrive'])
}

export interface EntityStore extends EntityState {
  setAuthToken: (token: string | null) => void;
  addEntity: (entity: BaseEntityType, parentId?: string) => void;
  deleteEntity: (id: string) => void;
  initialize: (spaceId: string) => Promise<void>;
  performDelete: (id: string) => Promise<void>;
  updateEntity: (entity: BaseEntityType) => void;
  setEntities: (entities: BaseEntityType[]) => void;
  fetchEntities: () => Promise<void>;
  undo: () => void;
  redo: () => void;
  logEvent: (event: Omit<EntityEvent, 'id' | 'timestamp'>) => void;
  cleanup: () => void;
  cleanupFn?: () => void;
  removeEntity: (entityId: string) => void;
  
  // Internal helpers
  _updateConnectedIntegrations: (entities: BaseEntityType[]) => Set<string>;
  onSpaceUpdate: (spaceId: string, update: SpaceUpdate) => void;
  
  // ✅ NEW: Background staleness check for cache validation
  checkAndSyncStaleEntities: () => Promise<void>;
  
  // Entity lookup helpers
  getEntityById: (id: string) => BaseEntityType | undefined;
  getEntityByLlmId: (id: string) => BaseEntityType | undefined;
  
  // ✅ PHASE 2: Entity filtering selectors (replaces HierarchyStore)
  getDisplayableEntities: () => BaseEntityType[];
  getTrashedEntities: () => BaseEntityType[];
  getSystemGroups: () => {
    spaceChatGroup: BaseEntityType | null;
  };
  
  // Internal mutation helpers (single code path for all mutations)
  _applyLocalUpsert: (entity: BaseEntityType, opts?: {
    log?: boolean;
    source?: 'local' | 'crdt' | 'event';
    syncCRDT?: boolean;
  }) => BaseEntityType;
  _applyLocalDelete: (id: string, opts?: {
    log?: boolean;
    source?: 'local' | 'crdt' | 'event';
    syncCRDT?: boolean;
  }) => void;
  
  // Entity actions (canonical mutation API)
  actions: {
    createFolder: (name: string, parentId?: string, options?: { color?: string, spaceId?: string, edge_pos?: string }) => Promise<BaseEntityType>;
    moveEntity: (entityId: string, targetParentId: string | null) => Promise<void>;
    createEntity: (data: Partial<BaseEntityType>) => Promise<BaseEntityType>;
    updateEntity: (entityId: string, data: Partial<BaseEntityType>) => Promise<BaseEntityType>;
    getEntity: (entityId: string) => Promise<BaseEntityType>;
    softDeleteEntity: (entityId: string) => Promise<void>;
    closeWindowEntity: (entityId: string) => Promise<void>;
    undeleteEntity: (entityId: string) => Promise<void>;
    groupEntities: (entityIds: string[], folderName: string, parentId?: string) => Promise<any>;
    ungroupEntities: (folderId: string) => Promise<void>;
  };
}

/**
 * Normalize entity for cache storage
 * - Strips base64/content from Doc entities (data loss prevention)
 * - Ensures required fields
 */
function normalizeEntityForCache(e: BaseEntityType): BaseEntityType {
  // Start with all fields from the entity, then override specific ones
  const baseEntity: BaseEntityType = {
    ...e,
    created_at: e.created_at || new Date().toISOString(),
    updated_at: e.updated_at || new Date().toISOString(),
    type: e.type || e.entityType,
    entityType: e.entityType || e.type,
  };

  // 🛡️ DATA LOSS PREVENTION: Never cache document content
  if (e.entityType === 'Doc' && e.skeleton) {
    return {
      ...baseEntity,
      skeleton: {
        ...e.skeleton,
        base64: undefined,          // ❌ Strip content (data loss prevention)
        content: undefined,         // ❌ Strip content (data loss prevention)
        // ✅ KEEP metadata (nodeCount, wordCount, lastSaved)
        // This ensures fresh metadata flows through when entities are updated
      },
      // ✅ ROBUSTNESS: Mark as cached so we can prevent accidental saves
      __fromCache: true,
    } as any;
  }

  return baseEntity;
}

/**
 * Flatten hierarchical entity tree to flat array
 * - Strips children property (buildEntityHierarchy will rebuild it)
 * - Used for merging and comparing entities
 */
function flattenEntities(entities: BaseEntityType[]): BaseEntityType[] {
  const result: BaseEntityType[] = [];
  const traverse = (entity: BaseEntityType) => {
    // Strip children property before adding (buildEntityHierarchy will rebuild it)
    const { children, ...entityWithoutChildren } = entity as any;
    result.push(entityWithoutChildren);
    if (children && Array.isArray(children)) {
      children.forEach(traverse);
    }
  };
  entities.forEach(traverse);
  return result;
}

const useEntityStore = create<EntityStore>((set, get) => {
  const log = createFileLogger('ENTITY_STORE', { enabled: false });
  
  // ✅ REMOVED: Manual cache update function
  // Cache sync is now handled automatically via Zustand subscription (see bottom of file)
  // This ensures 100% coverage of all entity mutations without manual intervention
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🔍 ENTITY FILTERING SELECTORS (Phase 2 Cleanup - Replaces HierarchyStore)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // ✅ Export constants for use in other files
  const SYSTEM_ENTITY_IDS = {
    SPACECHAT: '__spacechat__',
    INTEGRATIONS: '__integrations__',
  } as const;
  
  /**
   * Internal helper: Apply local entity upsert (create or update)
   * This is the SINGLE code path for all entity mutations
   */
  const _applyLocalUpsert = (
    entity: BaseEntityType,
    opts?: {
      log?: boolean;
      source?: 'local' | 'crdt' | 'event';
      syncCRDT?: boolean;
    }
  ): BaseEntityType => {
    const options = {
      log: true,
      source: 'local' as const,
      syncCRDT: true,
      ...opts
    };
    
    // ✅ RUNTIME GUARDS: Prevent critical bugs
    
    // 🔴 CRITICAL GUARD: Entity MUST have an _id
    if (!entity._id && !(entity as any).id) {
      console.error('[entityStore] 🔴 CRITICAL: Entity missing _id!', {
        entityName: entity.name,
        entityType: entity.entityType,
        allKeys: Object.keys(entity),
      });
      throw new Error(`[entityStore] Entity missing _id: ${entity.name} (${entity.entityType})`);
    }
    
    // Guard: base64 should not be in cache (prevents stale content)
    if (entity.entityType === 'Doc' && (entity.skeleton as any)?.base64) {
      console.error('[entityStore] ⚠️ RUNTIME GUARD: base64 in cache - should be stripped', {
        entityId: entity._id,
        name: entity.name,
        hasBase64: !!(entity.skeleton as any)?.base64
      });
    }
    
    // 🔴 CRITICAL GUARD: Prevent CRDT infinite loop (browser crash)
    // CRDT updates MUST NOT sync back to CRDT (creates infinite loop)
    if (options.source === 'crdt' && options.syncCRDT !== false) {
      const errorMsg = `[entityStore] 🔄 CRDT LOOP PREVENTED! CRDT updates must set syncCRDT=false. Entity: ${entity._id}`;
      console.error(errorMsg, {
        entityId: entity._id,
        source: options.source,
        syncCRDT: options.syncCRDT,
        stack: new Error().stack
      });
      // THROW to prevent infinite loop - this is critical
      throw new Error(errorMsg);
    }
    
    const normalized = normalizeEntityForCache(entity);
    const currentState = get();
    const spaceId = currentState.currentSpaceId;
    
    // Only log if it's a local action (not from CRDT/event)
    if (options.log && options.source === 'local') {
      const prev = currentState.entities.find((e) => e._id === normalized._id);
      get().logEvent({
        userId: 'current-user',
        action: prev ? 'update' : 'create',
        entityId: normalized._id,
        data: normalized,
        previousData: prev,
      });
    }
    
    // No explicit CRDT write needed here: the REST handler (backend/routes/entities.py)
    // already broadcasts to entity-system Y.Doc via POST /crdt/entity-system/.../update,
    // which syncs to all connected clients automatically via WebSocket.
    // (Previously used entityCRDTService.createEntity/updateEntity — removed to eliminate
    //  the sync feedback loop with entities:org:... room.)
    
    // Always update local state
    set((state) => {
      const existingIndex = state.entities.findIndex((e) => e._id === normalized._id);
      let newEntities: BaseEntityType[];
      
      if (existingIndex >= 0) {
        newEntities = [...state.entities];
        newEntities[existingIndex] = normalized;
        // log.log('🔄 [ENTITY_STORE] Entity updated:', {
        //   entityId: normalized._id,
        //   entityName: normalized.name,
        //   entityType: normalized.entityType,
        //   source: options.source,
        //   hasSpaceId: !!normalized.spaceId,
        //   spaceId: normalized.spaceId,
        //   groupIds: (normalized.skeleton as any)?.groupIds,
        //   childEntities: (normalized.skeleton as any)?.childEntities
        // });
      } else {
        newEntities = [...state.entities, normalized];
        // log.log('➕ [ENTITY_STORE] Entity added:', {
        //   entityId: normalized._id,
        //   entityName: normalized.name,
        //   entityType: normalized.entityType,
        //   source: options.source,
        //   groupIds: (normalized.skeleton as any)?.groupIds,
        //   childEntities: (normalized.skeleton as any)?.childEntities,
        //   totalEntities: newEntities.length
        // });
      }
      
      // ✅ Update computed state: connectedIntegrations
      const updatedConnectedIntegrations = get()._updateConnectedIntegrations(newEntities);
      
      return { 
        entities: buildEntityHierarchy(newEntities),
        connectedIntegrations: updatedConnectedIntegrations
      };
    });
    
    // ✅ IndexedDB sync handled automatically by subscription (see bottom of file)
    
    return normalized;
  };
  
  /**
   * Internal helper: Apply local entity delete
   * This is the SINGLE code path for all entity deletions
   */
  const _applyLocalDelete = (
    id: string,
    opts?: {
      log?: boolean;
      source?: 'local' | 'crdt' | 'event';
      syncCRDT?: boolean;
    }
  ): void => {
    const options = {
      log: true,
      source: 'local' as const,
      syncCRDT: true,
      ...opts
    };
    
    const currentState = get();
    const prev = currentState.entities.find((e) => e._id === id);
    const spaceId = currentState.currentSpaceId;
    
    // Only log if it's a local action (not from CRDT/event)
    if (options.log && options.source === 'local' && prev) {
      get().logEvent({
        userId: 'current-user',
        action: 'delete',
        entityId: id,
        previousData: prev,
      });
    }
    
    // Deletion is broadcast by the REST DELETE handler (backend/routes/entities.py)
    // via entity-system Y.Doc; no local CRDT write needed.
    
    // Always update local state
    set((state) => {
      const newEntities = state.entities.filter((e) => e._id !== id);
      
      // ✅ Update computed state: connectedIntegrations
      const updatedConnectedIntegrations = get()._updateConnectedIntegrations(newEntities);
      
      return {
        entities: newEntities,
        connectedIntegrations: updatedConnectedIntegrations
      };
    });

    // Delete the entity's y-indexeddb database (fire-and-forget, never blocks UI).
    // _applyLocalDelete is only called for permanent deletes; soft-deletes go through
    // _applyLocalUpsert with trashed:true and are not handled here.
    import('../services/sync/localFirstSyncService').then(({ localFirstSyncService }) => {
      localFirstSyncService.deleteEntityDoc(id)
    }).catch(() => {})

    // ✅ IndexedDB sync handled automatically by subscription (see bottom of file)
  };
  
  /**
   * Helper: Identify stale entities by comparing timestamps.
   * Defined as a variable so it can be referenced from checkAndSyncStaleEntities.
   */
  const identifyStaleEntities = (
    localTimestamps: Record<string, string>,
    backendTimestamps: Record<string, string>
  ): string[] => {
    const staleIds: string[] = [];
    
    // Check each entity on backend
    for (const [llmId, backendUpdatedAt] of Object.entries(backendTimestamps)) {
      const localUpdatedAt = localTimestamps[llmId];
      
      if (!localUpdatedAt) {
        // Entity exists on backend but not locally (new entity)
        log.info('[EntityStore] New entity detected', { llmId });
        staleIds.push(llmId);
        continue;
      }
      
      // Parse timestamps and compare
      const backendDate = new Date(backendUpdatedAt).getTime();
      const localDate = new Date(localUpdatedAt).getTime();
      
      if (backendDate > localDate) {
        // Backend version is newer
        log.info('[EntityStore] Updated entity detected', {
          llmId,
          localUpdatedAt,
          backendUpdatedAt
        });
        staleIds.push(llmId);
      }
    }
    
    // Optional: Detect deleted entities
    const deletedIds: string[] = [];
    for (const llmId of Object.keys(localTimestamps)) {
      if (!backendTimestamps[llmId]) {
        deletedIds.push(llmId);
      }
    }
    
    if (deletedIds.length > 0) {
      log.info('[EntityStore] Deleted entities detected - removing from local state', {
        count: deletedIds.length,
        ids: deletedIds.slice(0, 5)
      });
      
      const currentEntities = get().entities;
      for (const llmId of deletedIds) {
        const entity = currentEntities.find(e => (e.llmId || e._id || e.id) === llmId);
        if (entity) {
          _applyLocalDelete(entity._id || (entity as any).id, { source: 'local', log: false, syncCRDT: false });
        }
      }
    }
    
    return staleIds;
  };

  /**
   * Helper: Merge fresh entities from backend into current entity array.
   * Defined as a variable so it can be referenced from checkAndSyncStaleEntities.
   */
  const mergeEntities = (
    currentEntities: BaseEntityType[],
    freshEntities: BaseEntityType[]
  ): BaseEntityType[] => {
    const flatCurrent = flattenEntities(currentEntities);
    
    log.info('[EntityStore] Flattened current entities', {
      hierarchicalCount: currentEntities.length,
      flatCount: flatCurrent.length
    });
    
    // Create lookup map of fresh entities by llmId
    const freshMap = new Map<string, BaseEntityType>();
    for (const entity of freshEntities) {
      if (entity.llmId) {
        freshMap.set(entity.llmId, entity);
      }
    }
    
    // ✅ SIMPLIFIED: Just overwrite local with backend data (backend is source of truth)
    const mergedEntities = flatCurrent.map(entity => {
      if (entity.llmId && freshMap.has(entity.llmId)) {
        const fresh = freshMap.get(entity.llmId)!;
        log.info('[EntityStore] Overwriting local entity with backend version', {
          llmId: entity.llmId,
          oldName: entity.name,
          newName: fresh.name,
          oldUpdatedAt: entity.updated_at,
          newUpdatedAt: fresh.updated_at
        });
        freshMap.delete(entity.llmId); // Mark as processed
        return fresh;  // ✅ Always use backend version
      }
      return entity;
    });
    
    // Add any remaining fresh entities that weren't in current (new entities)
    for (const [llmId, freshEntity] of freshMap.entries()) {
      log.info('[EntityStore] Adding new entity from backend', {
        llmId,
        name: freshEntity.name
      });
      mergedEntities.push(freshEntity);
    }
    
    log.info('[EntityStore] Merge complete', {
      originalCount: flatCurrent.length,
      freshCount: freshEntities.length,
      mergedCount: mergedEntities.length
    });
    
    return mergedEntities;
  };
  

  return {
  entities: [],
  authToken: localStorage.getItem('authToken'),
  status: 'idle',
  events: [],
  undoStack: [],
  redoStack: [],
  initialized: false,
  isObserving: false,  // ✅ OPTIMIZATION: Track if CRDT observer is active
  currentSpaceId: undefined,
  initializationPromise: null,
  initializationEpoch: 0,
  lifecycle: 'uninitialized',  // ✅ NEW: Initial lifecycle state
  syncInProgress: false,  // ✅ NEW: Track concurrent staleness checks
  
  // ✅ ORGS V1: Separate entity state per dataspace
  dataspaceEntities: {
    personal: []
  },
  currentDataspaceKey: 'personal',
  
  // ✅ Computed state: Connected integrations (O(1) lookup)
  connectedIntegrations: new Set(),
  
  // Expose internal helpers
  _applyLocalUpsert,
  _applyLocalDelete,
  
  // ✅ Helper: Recompute connected integrations Set (O(n) but only on mutations)
  _updateConnectedIntegrations: (entities: BaseEntityType[]) => {
    const connected = entities
      .filter((e: any) => 
        (e.entityType === 'Integration' || e.entityType?.startsWith('Integration.')) &&
        e.skeleton?.status === 'connected'
      )
      .map((e: any) => e.skeleton?.integrationType)
      .filter(Boolean);
    
    return new Set(connected);
  },
  
  // Initialize entity actions (canonical mutation API)
  actions: createEntityActions(set, get),

  initialize: async (spaceId: string) => {
    const currentState = get();
    
    log.info('[EntityStore] 🚀 Initialize called', { spaceId, initialized: currentState.initialized, hasPromise: !!currentState.initializationPromise });

    // ✅ DEBUG: Log every initialize call to diagnose missing logs
    log.info('[EntityStore] 🚀 Initialize called for space:', spaceId);

    // ✅ CRITICAL FIX: Prevent duplicate initialization by checking in-flight promise
    // But only reuse the promise if it's for the SAME space ID
    if (currentState.initializationPromise && currentState.currentSpaceId === spaceId) {
      log.info('[EntityStore] ⚡ Initialization already in progress for same space, waiting for existing promise');
      return currentState.initializationPromise;
    }
    
    // If there's a promise for a DIFFERENT space, clear it and start fresh
    // This handles the case where App.tsx initializes with user-scoped ID, then SpacePageWrapper with space ID
    if (currentState.initializationPromise && currentState.currentSpaceId !== spaceId) {
      log.info('[EntityStore] 🔄 Clearing initialization promise for different space (was:', currentState.currentSpaceId, 'now:', spaceId, ')');
      set({ initializationPromise: null, initialized: false, lifecycle: 'uninitialized' });
    }

    // If already initialized and the lifecycle is ready, skip re-init entirely.
    // entityStore is dataspace-scoped (loads ALL user entities), not space-scoped.
    // Once 'ready', the entity cache is valid for every space in the current dataspace.
    // Callers may pass different ID formats (crdtRoomId vs spaceId); that difference must
    // not cause a redundant full entity fetch.
    // Dataspace changes are handled upstream (App.tsx resets initialized=false before
    // calling initialize() again), so this guard cannot hide a stale-dataspace scenario.
    if (currentState.initialized && currentState.lifecycle === 'ready') {
      log.info('[EntityStore] Already initialized and ready — skipping re-init');
      return;
    }

    // Clean up previous if needed
    if (currentState.cleanupFn) {
      currentState.cleanupFn();
    }

    // Capture the spaceId this IIFE is for. Any async step can be interrupted by a
    // subsequent initialize() call for a different space; we check this before
    // every final set() to avoid overwriting the newer initialization's state.
    const initForSpaceId = spaceId;

    const isStillCurrent = () => {
      const s = get();
      // A newer call cleared initializationPromise and set a different currentSpaceId
      return s.initializationPromise !== null || s.currentSpaceId === initForSpaceId || !s.currentSpaceId;
    };

    // Create and store the initialization promise
    const initPromise = (async () => {
      try {
        set({ status: 'loading', lifecycle: 'loading' });
        const perfStart = performance.now();
        log.info('[EntityStore] Fetching ALL user entities (not space-filtered)');
      
      // Check if we have authentication before trying to fetch
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        log.warn('[EntityStore] No auth token available, deferring initialization');
        set({ status: 'idle', lifecycle: 'uninitialized' });
        return;
      }
      
      // ✅ PERFORMANCE BOOST: Try IndexedDB cache first
      // Extract userId from JWT token (more reliable than localStorage)
      const userId = getCurrentUserId();
      
      // Early return if no valid user ID (not authenticated properly)
      if (!userId) {
        log.warn('[EntityStore] No valid userId available, fetching without cache');
        const fetchStart = performance.now();
        const fetchedEntities = await entityService.getAllUserEntities();
        const fetchTime = performance.now() - fetchStart;
        log.info(`⏱️ [PERF] Entity API fetch (no cache): ${fetchTime.toFixed(2)}ms | ${fetchedEntities.length} entities`);
        
        // Continue with normal flow (no caching) - jump to entity processing
        const entities = fetchedEntities.map(normalizeEntityForCache);
        
        // Initialize CRDT and set entities (same as cached path)
        const currentObservingState = get();
        if (!isStillCurrent()) {
          log.warn('[EntityStore] Space changed during init (no-userId path, observer check) — discarding');
          return;
        }
        if (currentObservingState.isObserving) {
          log.warn('[EntityStore] CRDT observer already active, skipping duplicate setup');
          if (!isStillCurrent()) {
            log.warn('[EntityStore] Space changed during init (no-userId path), discarding stale result');
            return;
          }
          set({ 
            entities: buildEntityHierarchy(entities), 
            status: 'idle',
            lifecycle: 'ready',
            initialized: true,
            currentSpaceId: initForSpaceId
          });
          return;
        }
        
        // Entity-system Y.Doc pushes updates to entityStore directly via its own
        // entitiesObserver in entitySystemYjsStore.ts (active after WS sync).
        // No separate subscription needed here — entitySystemYjsStore handles it.
        const cleanupCRDT = () => {};

        let cleanupSpaceCRDT: (() => void) | null = null;
        if (initForSpaceId !== 'default') {
          cleanupSpaceCRDT = spaceCRDTService.onSpaceUpdate(initForSpaceId, (update) => {
            if (update.windows) {
              set((state) => {
                const spaceEntity = state.entities.find((e) => e.entityType === 'Space' && e._id === initForSpaceId);
                
                if (isEqual(spaceEntity?.skeleton?.windows, update.windows)) {
                  return state;
                }
                
                const newEntities = state.entities.map((entity) => {
                  if (entity.entityType === 'Space' && entity._id === initForSpaceId) {
                    return {
                      ...entity,
                      skeleton: {
                        ...entity.skeleton,
                        windows: update.windows,
                      },
                    };
                  }
                  return entity;
                });
                
                return { entities: newEntities };
              });
            }
          });
        }
        
        // Bail if a newer initialize() call has taken over for a different space
        if (!isStillCurrent()) {
          log.warn('[EntityStore] Space changed during init (no-userId path), discarding stale result');
          cleanupCRDT();
          if (cleanupSpaceCRDT) cleanupSpaceCRDT();
          return;
        }


        // ✅ Update computed state
        const connectedIntegrations = get()._updateConnectedIntegrations(entities);
        
        set({
          entities: buildEntityHierarchy(entities),
          connectedIntegrations,
          status: 'idle',
          lifecycle: 'ready',
          isObserving: true,
          cleanupFn: () => {
            cleanupCRDT();
            if (cleanupSpaceCRDT) {
              cleanupSpaceCRDT();
            }
            set({ isObserving: false });
          },
          currentSpaceId: initForSpaceId,
          initialized: true,
        });
        
        const totalTime = performance.now() - perfStart;
        log.info(`📊 [PERF SUMMARY] No cache (no userId) - Total time: ${totalTime.toFixed(2)}ms`);
        return;
      }
      
      // ── Fetch entities from REST (authoritative source) ──────────────
      // Entity loading is always REST-based. The org Y.Doc only carries lightweight
      // metadata (name, icon, entityType) for real-time sync; it is not a full
      // entity cache.
      const fetchStart = performance.now();
      const fetchedEntities = await entityService.getAllUserEntities();
      const fetchTime = performance.now() - fetchStart;
      log.info(`⏱️ [PERF] Entity API fetch: ${fetchTime.toFixed(2)}ms | ${fetchedEntities.length} entities`);

      // 🛡️ CRITICAL: Backend already strips large data, frontend adds extra safety
      // ========================================================================
      // BACKEND STRIPPING (in /api/entities):
      // - Message entities: Excluded entirely (fetch per-thread)
      // - Doc entities: base64 field stripped
      // - MediaGen.Asset.Image: skeleton.url (base64) stripped, replaced with null
      // - Space entities: windows.{id}.entity (nested docs) stripped
      // ========================================================================
      // FRONTEND ADDITIONAL STRIPPING (for cache safety):
      // - Doc entities: Strip base64 + content (prevents stale content bugs)
      // ========================================================================
      const entities = fetchedEntities.map(normalizeEntityForCache);

      // ✅ User-scoped rooms (e.g., "user:auth0|...") are valid CRDT rooms
      // They provide real-time sync scoped to a single user across all their spaces

      // ✅ OPTIMIZATION: Prevent duplicate CRDT observers
      const currentObservingState = get();
      if (!isStillCurrent()) {
        log.warn('[EntityStore] Space changed during init (main path, observer check) — discarding REST results');
        return;
      }
      if (currentObservingState.isObserving) {
        log.warn('[EntityStore] CRDT observer already active, skipping duplicate setup');
        if (!isStillCurrent()) {
          log.warn('[EntityStore] Space changed during init, discarding stale result');
          return;
        }
        set({ 
          entities: buildEntityHierarchy(entities), 
          status: 'idle',
          lifecycle: 'ready',
          initialized: true,
          currentSpaceId: initForSpaceId
        });
        return;
      }

      // Entity-system Y.Doc pushes updates to entityStore directly via its own
      // entitiesObserver in entitySystemYjsStore.ts (active after WS sync).
      // No separate subscription needed here.
      const cleanupCRDT = () => {};

      // ✅ NEW: Set up entity event listener for cross-service synchronization
      // This catches entity changes from FileService, WordImport, etc. that bypass CRDT
      let cleanupEntityEvents: (() => void) | null = null;
      try {
        const { entityEventService } = await import('../services/entityEventService');
        log.info('[EntityStore] ✅ Setting up entity event listener (listening to ALL spaces)');
        
        cleanupEntityEvents = entityEventService.onEntityEvent('*', (event) => {
          // ✅ CRITICAL FIX: Don't filter by spaceId
          // EntityStore manages ALL user entities across ALL spaces,
          // so we need to process events from any space the user has access to
          
          // ✅ ARCHITECTURE FIX: Ignore 'local' events - they were already processed by the action
          // that called _applyLocalUpsert. Only process 'remote' or 'crdt' events.
          if (event.source === 'local') {
            log.log('[EntityStore] 📨 Ignoring local event (already processed by action):', {
              type: event.type,
              entityId: event.entity._id,
              entityName: event.entity.name
            });
            return;
          }
          
          log.log('[EntityStore] 📨 Entity event received:', {
            type: event.type,
            entityId: event.entity._id,
            entityName: event.entity.name,
            entityType: event.entity.entityType,
            source: event.source,
            spaceId: event.spaceId
          });

          if (event.type === 'created' || event.type === 'updated') {
            // Use helper for event updates (no logging, no CRDT sync - caller already did it)
            get()._applyLocalUpsert(event.entity, {
              source: 'event',
              log: false,
              syncCRDT: false
            });
            log.log('[EntityStore] ✅ Synced entity from event:', event.entity._id);
          } else if (event.type === 'deleted') {
            // Use helper for event deletions (no logging, no CRDT sync)
            get()._applyLocalDelete(event.entity._id, {
              source: 'event',
              log: false,
              syncCRDT: false
            });
            log.log('[EntityStore] ✅ Removed entity from event:', event.entity._id);
          }
        });
        
        log.info('[EntityStore] ✅ Entity event listener registered successfully');
      } catch (error) {
        log.error('[EntityStore] ❌ Failed to setup entity event listener:', error);
      }

      // Add Space CRDT observer
      let cleanupSpaceCRDT: (() => void) | null = null;
      if (initForSpaceId !== 'default') {
        cleanupSpaceCRDT = spaceCRDTService.onSpaceUpdate(initForSpaceId, (update) => {
          if (update.windows) {
            set((state) => {
              const spaceEntity = state.entities.find((e) => e.entityType === 'Space' && e._id === initForSpaceId);

              if (isEqual(spaceEntity?.skeleton?.windows, update.windows)) {
                return state;
              }

              const newEntities = state.entities.map((entity) => {
                if (entity.entityType === 'Space' && entity._id === initForSpaceId) {
                  return {
                    ...entity,
                    skeleton: {
                      ...entity.skeleton,
                      windows: update.windows,
                    },
                  };
                }
                return entity;
              });

              return { entities: newEntities };
            });
          }
        });
      }

      // Bail if a newer initialize() call has taken over for a different space
      if (!isStillCurrent()) {
        log.warn('[EntityStore] Space changed during initialization, discarding stale result for:', initForSpaceId);
        cleanupCRDT();
        if (cleanupEntityEvents) cleanupEntityEvents();
        if (cleanupSpaceCRDT) cleanupSpaceCRDT();
        return;
      }

      // ✅ Update computed state
      const connectedIntegrations = get()._updateConnectedIntegrations(entities);
      
      set({
        entities: buildEntityHierarchy(entities),
        connectedIntegrations,
        status: 'idle',
        lifecycle: 'ready',
        isObserving: true,
        cleanupFn: () => {
          cleanupCRDT();
          if (cleanupEntityEvents) {
            cleanupEntityEvents();
          }
          if (cleanupSpaceCRDT) {
            cleanupSpaceCRDT();
          }
          set({ isObserving: false });
        },
        currentSpaceId: initForSpaceId,
        initialized: true,
      });
      
      const totalTime = performance.now() - perfStart;
      log.info(`📊 [PERF SUMMARY] REST fetch - Total time: ${totalTime.toFixed(2)}ms`);
      log.info(`⏱️ [PERF] EntityStore initialization COMPLETE: ${totalTime.toFixed(2)}ms total`);
      
      // Sync fresh entities with window entities (deferred to avoid circular imports).
      setTimeout(async () => {
        // Guard: abort if a newer initialize() has taken over for a different space.
        if (!isStillCurrent()) {
          log.warn('[EntityStore] Space changed before window sync — discarding');
          return;
        }
        try {
          const { useSpaceStore } = await import('./spaceStore');
          const spaceStore = useSpaceStore.getState();

          log.info('[EntityStore] ✅ Syncing fresh entities with window entities');

          let syncCount = 0;
          Object.entries(spaceStore.windows).forEach(([windowId, windowEntity]) => {
            if (windowEntity.entity?._id) {
              const freshEntity = entities.find(e => e._id === windowEntity.entity._id);

              if (freshEntity && freshEntity.llmId && !windowEntity.entity.llmId) {
                const currentEntity = windowEntity.entity;
                const entityChanged =
                  !currentEntity ||
                  currentEntity._id !== freshEntity._id ||
                  currentEntity.name !== freshEntity.name ||
                  currentEntity.updated_at !== freshEntity.updated_at;

                if (entityChanged) {
                  spaceStore.updateWindow(windowId, { entity: freshEntity });
                  syncCount++;
                }
              }
            }
          });

          log.info('[EntityStore] 🎯 Entity sync complete:', {
            totalWindows: Object.keys(spaceStore.windows).length,
            entitiesUpdated: syncCount
          });
        } catch (error) {
          log.error('[EntityStore] Failed to sync entities with windows:', error);
        }
      }, 0);
    } catch (error) {
      log.error('[EntityStore] Failed to initialize:', error);
      
      // Check if it's an authentication error
      if (error instanceof Error && (error.message.includes('authenticated') || error.message.includes('401'))) {
        log.warn('[EntityStore] Authentication error during initialization - will retry when auth is available');
        set({ 
          status: 'idle',
          lifecycle: 'uninitialized',  // ✅ NEW: Reset lifecycle on auth error
          initialized: false,
          error: undefined,
          cleanupFn: undefined,
          initializationPromise: null,  // ✅ Clear promise on auth error
        });
        // Don't throw on auth errors - let SpaceStore handle gracefully
        return;
      } else {
        set({
          status: 'error',
          lifecycle: 'error',  // ✅ NEW: Mark as error
          error: error as Error,
          initialized: false,
          cleanupFn: undefined,
          initializationPromise: null,  // ✅ Clear promise on error
        });
        throw error;
      }
    } finally {
      // ✅ CRITICAL: Always clear the promise when done (success or error)
      set({ initializationPromise: null });
    }
    })();

    // Store the promise so subsequent calls can wait for it
    set({ initializationPromise: initPromise });

    // Return the promise so callers can await it
    return initPromise;
  },

  addEntity: (entity: BaseEntityType, _parentId?: string) => {
    // Thin wrapper around _applyLocalUpsert for backward compatibility
    _applyLocalUpsert(entity, {
      log: true,
      source: 'local',
      syncCRDT: true
    });
  },

  deleteEntity: (id) => {
    // Thin wrapper around _applyLocalDelete for backward compatibility
    _applyLocalDelete(id, {
      log: true,
      source: 'local',
      syncCRDT: true
    });
  },

  updateEntity: (entity) => {
    // Thin wrapper around _applyLocalUpsert for backward compatibility
    _applyLocalUpsert(entity, {
      log: true,
      source: 'local',
      syncCRDT: true
    });
  },

  removeEntity: (entityId: string) => {
    set((state) => {
      // Helper to remove entity from tree
      const removeFromTree = (entities: BaseEntityType[]): BaseEntityType[] => {
        return entities.filter((e) => {
          if (e._id === entityId) return false;
          if (e.children) {
            e.children = removeFromTree(e.children);
          }
          return true;
        });
      };

      return {
        entities: removeFromTree(state.entities),
      };
    });
  },

  cleanup: () => {
    const state = get();
    
    // entity-system Y.Doc observer cleanup is handled by the cleanupFn below.
    // (entityCRDTService.cleanup removed — entityCRDTService no longer used here.)
    
    // Cleanup other resources
    if (state.cleanupFn) {
      state.cleanupFn();
      set({
        initialized: false,
        cleanupFn: undefined,
        isObserving: false,
        currentSpaceId: undefined,
      });
    }
    
    // ✅ CRITICAL: Reset hierarchy store when cleaning up entities
    // This ensures rebuildCount returns to 0, so components wait for new hierarchy
    // Without this, switching spaces shows stale entities from previous space
    // NOTE: Only reset if we're actually switching spaces (have a currentSpaceId)
    // Don't reset on initial cleanup or logout scenarios
    // ✅ PHASE 2: No hierarchyStore to reset - filtering is done via selectors
    if (state.currentSpaceId) {
      log.info('[EntityStore] Space switch cleanup - filtering ready');
    }
    
    // ✅ BUG FIX #3: Clear memoization caches on cleanup
    // Prevents memory leaks and stale data when switching spaces or logging out
    cachedDisplayableEntities = [];
    lastEntitiesRef = null;
    cachedTrashedEntities = [];
    lastTrashedRef = null;
    log.info('[EntityStore] ✅ Memoization caches cleared');
    
    // Clear IndexedDB cache (for logout)
    const userId = getCurrentUserId();
    if (userId) {
      entityCacheService.delete(userId).catch(error => {
        log.error('[EntityStore] Failed to clear cache on cleanup:', error);
      });
    }
  },

  /**
   * ✅ NEW: Check if cached entities are stale and sync only what changed.
   * This is ALWAYS non-blocking and runs in the background.
   */
  checkAndSyncStaleEntities: async () => {
    const userId = getCurrentUserId();
    if (!userId) {
      log.warn('[EntityStore] No userId, skipping staleness check');
      return;
    }
    
    // ✅ FIX: Prevent concurrent staleness checks
    if (get().syncInProgress) {
      log.info('[EntityStore] Staleness check already in progress, skipping');
      return;
    }
    
    set({ syncInProgress: true });
    
    try {
      // STEP 1: Get Local Cache Data (dataspace-aware)
      // ✅ FIX: Use dataspace-aware cache key (same as initialize())
      const { getCurrentDataspaceCacheKey } = await import('../utils/entityCacheUtils');
      const cacheKey = getCurrentDataspaceCacheKey(userId);
      
      log.info('[EntityStore] 🔎 Looking up cache for staleness check', {
        cacheKey: cacheKey.substring(0, 50) + '...'
      });
      
      const cached = await entityCacheService.get(cacheKey);
      if (!cached || !cached.entitiesHash || !cached.entityTimestamps) {
        log.warn('[EntityStore] No cache or missing hash/timestamps, skipping staleness check', {
          hasCached: !!cached,
          hasHash: !!(cached?.entitiesHash),
          hasTimestamps: !!(cached?.entityTimestamps),
          cacheKey: cacheKey.substring(0, 50) + '...'
        });
        return;
      }
      
      const localHash = cached.entitiesHash;
      const localTimestamps = cached.entityTimestamps;
      
      log.info('[EntityStore] 🔍 Starting staleness check', {
        localHash: localHash,  // Full hash for comparison
        localHashPreview: localHash.substring(0, 8),
        localEntityCount: Object.keys(localTimestamps).length,
        sampleLocalEntities: Object.keys(localTimestamps).slice(0, 5)
      });
      
      // STEP 2: Fetch Backend Timestamps (Optimized)
      // ⚡ OPTIMIZED: Only fetches llmId + updated_at (5KB vs 200KB)
      log.info('[EntityStore] 🚀 Fetching backend timestamps (optimized)');
      
      const perfStart = performance.now();
      const backendTimestamps = await entityService.getEntityTimestamps();
      const fetchTime = performance.now() - perfStart;
      
      log.info('[EntityStore] ✅ Backend timestamps fetched', {
        count: Object.keys(backendTimestamps).length,
        fetchTimeMs: fetchTime.toFixed(2),
        estimatedSizeKB: (JSON.stringify(backendTimestamps).length / 1024).toFixed(2),
        sampleTimestamps: Object.entries(backendTimestamps).slice(0, 3).map(([llmId, ts]) => ({
          llmId: llmId.substring(0, 8) + '...',
          timestamp: ts
        })),
        allEntityIds: Object.keys(backendTimestamps).sort()
      });
      
      // STEP 3: Compute Backend Hash
      const backendHash = entityCacheService.computeEntitiesHash(backendTimestamps);
      
      log.info('[EntityStore] 🔑 Computed backend hash', {
        backendHash: backendHash,  // Full hash for comparison
        backendHashPreview: backendHash.substring(0, 8),
        backendEntityCount: Object.keys(backendTimestamps).length,
        sampleBackendEntities: Object.keys(backendTimestamps).slice(0, 5)
      });
      
      // STEP 4: Compare Hashes
      if (localHash === backendHash) {
        log.info('[EntityStore] ✅ Cache is fresh (hashes match), no sync needed', {
          matchingHash: localHash
        });
        return;
      }
      
      log.info('[EntityStore] ⚠️ Cache is stale (hashes differ), identifying changed entities', {
        localHash: localHash,
        backendHash: backendHash,
        localCount: Object.keys(localTimestamps).length,
        backendCount: Object.keys(backendTimestamps).length
      });
      
      // STEP 5: Identify Stale Entities
      const staleEntityIds = identifyStaleEntities(localTimestamps, backendTimestamps);
      
      if (staleEntityIds.length === 0) {
        log.info('[EntityStore] No stale entities identified (possibly only deletions)');
        return;
      }
      
      log.info('[EntityStore] 📝 Identified stale entities', {
        count: staleEntityIds.length,
        percentage: `${((staleEntityIds.length / Object.keys(backendTimestamps).length) * 100).toFixed(1)}%`,
        staleIds: staleEntityIds,
        details: staleEntityIds.slice(0, 3).map((id: string) => ({
          llmId: id,
          localTime: localTimestamps[id] || 'NEW',
          backendTime: backendTimestamps[id]
        }))
      });
      
      // STEP 6: Fetch Only Stale Entities
      const freshEntitiesRaw = await entityService.queryEntities({
        ids: staleEntityIds.join(',')
      });
      
      log.info('[EntityStore] 📥 Fetched fresh entities', {
        requested: staleEntityIds.length,
        received: freshEntitiesRaw.length,
        receivedNames: freshEntitiesRaw.map(e => e.name),
        missedCount: staleEntityIds.length - freshEntitiesRaw.length
      });
      
      // ✅ FIX: Normalize fresh entities to strip base64 content
      const freshEntities = freshEntitiesRaw.map(normalizeEntityForCache);
      
      log.info('[EntityStore] ✂️ Normalized fresh entities (stripped base64)', {
        normalizedCount: freshEntities.length
      });
      
      // STEP 7: Merge Fresh Entities into Current State
      const currentEntities = get().entities;
      const mergedEntities = mergeEntities(currentEntities, freshEntities);
      
      // STEP 8: Update Store (Triggers IndexedDB Sync Automatically)
      const connectedIntegrations = get()._updateConnectedIntegrations(mergedEntities);
      set({ 
        entities: buildEntityHierarchy(mergedEntities),
        connectedIntegrations
      });
      
      log.info('[EntityStore] ✅ Background sync complete', {
        totalEntities: mergedEntities.length,
        updatedEntities: freshEntities.length
      });
      
    } catch (error) {
      // Don't throw - background sync failures shouldn't break app
      log.error('[EntityStore] Background staleness check failed:', error);
    } finally {
      // ✅ CRITICAL: Always clear sync flag
      set({ syncInProgress: false });
    }
  },

  setAuthToken: (token: string | null) => {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
    set({ authToken: token });
  },

  setEntities: (entities: BaseEntityType[]) => {
    // ⚠️ WARNING: This bypasses the mutation funnel and should be avoided
    const connectedIntegrations = get()._updateConnectedIntegrations(entities);
    set({ 
      entities: buildEntityHierarchy(entities),
      connectedIntegrations
    });
  },

  fetchEntities: async () => {
    set({ status: 'loading' });

    try {
      const spaceId = get().currentSpaceId;
      if (!spaceId) {
        throw new Error('No space ID set');
      }

      console.log('[EntityStore] 📡 fetchEntities called:', JSON.stringify({
        currentSpaceId: spaceId,
        beforeFetch_entityCount: get().entities.length
      }, null, 2));

      // Don't pass 'default' as parentId - it should be undefined for the default space
      const parentId = spaceId === 'default' ? undefined : spaceId;
      const fetchedEntities = await fileService.getList(parentId);

      console.log('[EntityStore] 📥 Fetched entities from API:', JSON.stringify({
        parentId,
        fetchedCount: fetchedEntities.length,
        entityTypes: Array.from(new Set(fetchedEntities.map((e: any) => e.entityType))),
        entityTypeCounts: fetchedEntities.reduce((acc: any, e: any) => {
          acc[e.entityType] = (acc[e.entityType] || 0) + 1;
          return acc;
        }, {}),
        sampleEntities: fetchedEntities.slice(0, 5).map((e: any) => ({
          _id: e._id,
          llmId: e.llmId,
          name: e.name,
          entityType: e.entityType,
          spaceId: e.spaceId
        }))
      }, null, 2));

      // ✅ OPTIMIZATION: Don't pre-populate CRDT with all entities to avoid WebSocket payload size issues
      // CRDT should only track incremental updates (create/update/delete), not full entity lists

      // Normalize entities (strip large content blobs, etc.) and update
      // connectedIntegrations — same pipeline as the primary initialize() path.
      const normalizedEntities = fetchedEntities.map(normalizeEntityForCache);
      const connectedIntegrations = get()._updateConnectedIntegrations(normalizedEntities);
      set({
        entities: buildEntityHierarchy(normalizedEntities),
        connectedIntegrations,
        status: 'idle',
      });
      
      console.log('[EntityStore] ✅ Entities set in store:', JSON.stringify({
        totalEntities: get().entities.length,
        entityTypes: Array.from(new Set(get().entities.map((e: any) => e.entityType)))
      }, null, 2));
    } catch (error) {
      console.error('[EntityStore] ❌ fetchEntities failed:', JSON.stringify({
        error: error instanceof Error ? error.message : String(error),
        currentSpaceId: get().currentSpaceId
      }, null, 2));
      set({ status: 'error', error: error as Error });
      throw error;
    }
  },

  performDelete: async (id: string) => {
    try {
      // Immediately update local state for responsive UI
      get().deleteEntity(id);

      // Then try backend and CRDT updates in parallel
      try {
        const spaceId = get().currentSpaceId || '';
        // REST delete broadcasts to entity-system Y.Doc; no separate CRDT call needed.
        const [backendResult] = await Promise.all([entityService.deleteEntity(id)]);

        if (!backendResult) {
          // Entity might already be deleted
        }
      } catch (error) {
        // Fetch fresh entities to restore state
        await get().fetchEntities();
        throw error;
      }
    } catch (error) {
      throw error;
    }
  },

  logEvent: (eventData) => {
    const event: EntityEvent = {
      ...eventData,
      id: uuidv4(),
      timestamp: Date.now(),
    };

    // Cap undo/redo/event stacks to prevent unbounded memory growth in long sessions.
    const MAX_STACK = 100;
    set((state) => ({
      events: [...state.events, event].slice(-MAX_STACK),
      undoStack: [...state.undoStack, event].slice(-MAX_STACK),
      redoStack: [], // Clear redo stack when new event occurs
    }));
  },

  undo: () => {
    const state = get();
    const lastEvent = state.undoStack[state.undoStack.length - 1];

    if (!lastEvent) return;

    // Remove from undo stack and add to redo stack
    set((state) => ({
      undoStack: state.undoStack.slice(0, -1),
      redoStack: [...state.redoStack, lastEvent],
    }));

    // Reverse the action using helpers (log: false to avoid adding undo action during undo)
    switch (lastEvent.action) {
      case 'create':
        _applyLocalDelete(lastEvent.entityId, {
          log: false,
          source: 'local',
          syncCRDT: true
        });
        break;
      case 'update':
        if (lastEvent.previousData) {
          _applyLocalUpsert(lastEvent.previousData, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
        }
        break;
      case 'delete':
        if (lastEvent.previousData) {
          // ✅ CRITICAL: Ensure entity is not marked as trashed when restoring
          const restoredEntity = { 
            ...lastEvent.previousData, 
            trashed: false 
          };
          
          _applyLocalUpsert(restoredEntity, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
          
          // ✅ CRITICAL: If restoring a Group, also restore its children's groupIds
          if (restoredEntity.entityType === 'Group') {
            const childEntityIds = restoredEntity.skeleton?.childEntities || [];
            
            // Update each child to point back to this folder
            for (const childId of childEntityIds) {
              const child = get().entities.find(e => e._id === childId);
              if (child) {
                _applyLocalUpsert({
                  ...child,
                  skeleton: {
                    ...child.skeleton,
                    groupIds: [lastEvent.entityId]
                  }
                }, {
                  log: false,
                  source: 'local',
                  syncCRDT: true
                });
              }
            }
          }
        }
        break;
    }
  },

  redo: () => {
    const state = get();
    const lastRedoEvent = state.redoStack[state.redoStack.length - 1];

    if (!lastRedoEvent) return;

    // Remove from redo stack and add to undo stack
    set((state) => ({
      redoStack: state.redoStack.slice(0, -1),
      undoStack: [...state.undoStack, lastRedoEvent],
    }));

    // Replay the action using helpers (log: false to avoid adding undo action during redo)
    switch (lastRedoEvent.action) {
      case 'create':
        if (lastRedoEvent.data) {
          _applyLocalUpsert(lastRedoEvent.data, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
        }
        break;
      case 'update':
        if (lastRedoEvent.data) {
          _applyLocalUpsert(lastRedoEvent.data, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
        }
        break;
      case 'delete':
        _applyLocalDelete(lastRedoEvent.entityId, {
          log: false,
          source: 'local',
          syncCRDT: true
        });
        break;
    }
  },

  // Debounce the space update handler
  onSpaceUpdate: debounce((spaceId: string, update: SpaceUpdate) => {
    if (update.windows) {
      // ✅ FIX: Use helper instead of direct state mutation
      const spaceEntity = get().entities.find((e) => e.entityType === 'Space' && e._id === spaceId);
      
      if (spaceEntity) {
        _applyLocalUpsert({
          ...spaceEntity,
          skeleton: {
            ...spaceEntity.skeleton,
            windows: update.windows,
          },
        }, {
          source: 'local',
          syncCRDT: false, // Don't sync back (this came from space updates)
          log: false
        });
      }
    }
  }, 100),
  
  getEntityById: (id: string) => {
    return get().entities.find(e => e._id === id || e.id === id);
  },

  getEntityByLlmId: (id: string) => {
    return get().entities.find(e => e.llmId === id || e._id === id);
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔍 ENTITY FILTERING SELECTORS (Phase 2 Cleanup - Replaces HierarchyStore)
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Get displayable entities (filtered by type and trash status)
   * Filters out system entities that shouldn't appear in the sidebar
   */
  getDisplayableEntities: () => {
    const state = get();
    const entities = state.entities;
    
    // ✅ BUG FIX #1: Return cached result if entities reference hasn't changed
    // Changed: 'cachedDisplayableEntities.length > 0' → 'lastEntitiesRef !== null'
    // Reason: The old condition failed when result was legitimately empty (no files)
    // This caused infinite re-renders when sidebar was empty
    if (entities === lastEntitiesRef && lastEntitiesRef !== null) {
      return cachedDisplayableEntities;
    }
    
    // Filter to displayable entities only
    const displayableEntities = entities.filter((entity) => {
      // Filter out trashed entities
      if ((entity as any).trashed === true) {
        return false;
      }
      
      // ⚡ OPTIMIZED: Use Set.has() for O(1) lookup instead of multiple if checks
      if (HIDDEN_ENTITY_TYPES.has(entity.entityType) || (entity.skeleton as any)?.isDuplicate) {
        return false;
      }
      
      // Filter out all Integration sub-types (Integration.Gmail, Integration.GitHub, etc.)
      // mailAppStore reads entityStore.entities directly so this doesn't affect its lookups.
      if (entity.entityType?.startsWith('Integration.')) {
        return false;
      }

      // Filter out LocalFile only (GoogleDrive files are now user-visible in sidebar)
      if (entity.entityType === 'LocalFile' ||
          entity.entityType?.startsWith('External.LocalFile')) {
        return false;
      }
      
      // Filter out embedded entities
      const skeleton = (entity.skeleton as any) || {};
      if (skeleton.isEmbedded === true || skeleton.embedded === true || skeleton.parentEntityId) {
        return false;
      }
      
      // Filter pasted images by naming pattern
      if (entity.entityType?.startsWith('File.Image')) {
        const isPastedImage = entity.name?.match(/^pasted-image-\d+\.(png|jpg|jpeg|gif|webp)$/i);
        if (isPastedImage) {
          return false;
        }
      }
      
      return true;
    });
    
    // Create synthetic system groups if needed
    
    // Add Chats group if there are orphaned threads.
    // Orphaned threads are moved *into* the synthetic group and removed from the
    // flat list so they don't appear twice (once at root, once under Chats).
    const threads = displayableEntities.filter(e => e.entityType === 'Thread');
    const orphanedThreads = threads.filter(t => {
      const groupIds = (t.skeleton as any)?.groupIds || [];
      return groupIds.length === 0;
    });
    const orphanedThreadIds = new Set(orphanedThreads.map(t => t._id));
    
    // Exclude orphaned threads from the flat list — they belong inside the Chats group
    const result = displayableEntities.filter(e => !orphanedThreadIds.has(e._id));
    
    if (orphanedThreads.length > 0) {
      const spaceChatEntity: BaseEntityType = {
        _id: SYSTEM_ENTITY_IDS.SPACECHAT,
        entityType: 'Group',
        name: 'Chats',
        skeleton: {
          '@type': 'Group',
          groupName: 'Chats',
          childEntities: orphanedThreads.map(t => t._id),
          isSystemGroup: true,
        } as any,
        children: orphanedThreads,
        created_at: STABLE_SYSTEM_TIMESTAMP,
        updated_at: STABLE_SYSTEM_TIMESTAMP,
      } as BaseEntityType;
      
      result.unshift(spaceChatEntity);
    }
    
    // ✅ Update cache to prevent infinite loops
    lastEntitiesRef = entities;
    cachedDisplayableEntities = result;
    
    return result;
  },
  
  /**
   * Get all trashed entities
   * ✅ Memoized to prevent infinite re-render loops
   */
  getTrashedEntities: () => {
    const state = get();
    const entities = state.entities;
    
    // ✅ Memoization: Return cached result if entities reference hasn't changed
    if (entities === lastTrashedRef && lastTrashedRef !== null) {
      return cachedTrashedEntities;
    }
    
    const trashed = entities.filter(entity => {
      if ((entity as any).trashed !== true) {
        return false;
      }
      
      if ((entity as any).hidden === true) {
        return false;
      }
      
      // ⚡ OPTIMIZED: Use Set.has() for O(1) lookup
      if (HIDDEN_ENTITY_TYPES.has(entity.entityType) || (entity.skeleton as any)?.isDuplicate) {
        return false;
      }
      
      // Also hide hierarchical Integration types (Integration.Gmail, Integration.Calendar, etc.)
      if (entity.entityType?.startsWith('Integration.')) {
        return false;
      }
      
      return true;
    });
    
    // ✅ Update cache to prevent infinite loops
    lastTrashedRef = entities;
    cachedTrashedEntities = trashed;
    
    return trashed;
  },
  
  /**
   * Get synthetic system groups (Chats)
   */
  getSystemGroups: () => {
    const displayableEntities = get().getDisplayableEntities();
    const spaceChatGroup = displayableEntities.find(e => e._id === SYSTEM_ENTITY_IDS.SPACECHAT) || null;
    return { spaceChatGroup };
  },
};
});

const buildEntityHierarchy = (entities: BaseEntityType[]): BaseEntityType[] => {
  // ✅ PERFORMANCE FIX: Return ALL entities as flat list
  // Components will build their own hierarchies as needed using buildEntityBrowserApp2Hierarchy
  // This avoids double-processing and allows different components to use different hierarchy views
  return entities;
};

export const getEntityStore = () => {
  const entityStore = useEntityStore.getState();
  return entityStore;
}

export default useEntityStore;

/**
 * System entity IDs for synthetic groups
 * Re-exported for compatibility with files that previously imported from hierarchyStore
 */
export const SYSTEM_ENTITY_IDS = {
  SPACECHAT: '__spacechat__',
  INTEGRATIONS: '__integrations__',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// 🔄 AUTOMATIC INDEXEDDB SYNC VIA ZUSTAND SUBSCRIPTION
// ═══════════════════════════════════════════════════════════════════════════
//
// ARCHITECTURE:
// - Subscribes to ALL entity changes (create, update, delete, CRDT, events)
// - Automatically syncs to IndexedDB when entities reference changes
// - 100% coverage - impossible to miss a mutation point
// - Single source of truth for cache sync logic
//
// GUARDS:
// 1. Reference equality - only syncs when entities array reference changes
// 2. Lifecycle check - skips during 'loading' and 'uninitialized' states
// 3. UserId check - must have authenticated user
//
// BENEFITS:
// - Robust: Catches all 12+ mutation points automatically
// - Maintainable: No need to remember manual cache calls
// - Clean: Centralized sync logic in one place
// - Safe: Guards prevent syncing during initial load
//
// ═══════════════════════════════════════════════════════════════════════════

// entityStore no longer maintains its own IndexedDB cache.
// The entity-system Y.Doc's y-indexeddb (managed by entitySystemYjsStore) is the
// correct caching layer — it is automatically kept in sync by Y.js while the client
// is connected, and _seedEntitiesFromMap reconciles entityStore from it on startup.
// A separate entityStore cache only introduced stale-data bugs.

// ─── Performance canary: detect entity array resets ──────────────────────────
// Finding #2: the systemStore init effect resets entities=[] when the dataspace
// key mismatches. After fixing finding #2, entityStoreResetCount should stay ≤ 1.
// This subscription is no-op in production (perf.increment is a no-op when IS_PROD).
import('../utils/perfInstrumentation').then(({ perf }) => {
  let prevLength = useEntityStore.getState().entities.length;
  useEntityStore.subscribe((state) => {
    const len = state.entities.length;
    if (len === 0 && prevLength > 0) {
      perf.increment('entityStoreResetCount');
    }
    prevLength = len;
  });
}).catch(() => {});
